import { useEffect, useRef, useState } from 'react';
import { ChallengeSetupScreen, ChallengeInviteScreen, PendingChallengesScreen } from './ChallengeScreens';
import LocationScreen from './LocationScreen';
import RankingScreen from './RankingScreen';
import MapHubScreen from './MapHubScreen';
import OnboardingScreen from './OnboardingScreen';
import ResultScreen from './ResultScreen';
import CreditsScreen from './CreditsScreen';
import ReviewScreen from './ReviewScreen';
import {
  checkIdentifier,
  loadPlayerSession,
  registerPlayer,
  requestLoginLink,
  resendVerificationCode,
  restorePlayerSession,
  savePlayerSession,
  verifyEmailCode,
} from '../../infrastructure/playerAuthApi';
import { subscribeEventOptin } from '../../infrastructure/eventOptinApi';
import { fetchRanking } from '../../infrastructure/rankingApi';
import { fetchReview, markReviewSeen } from '../../infrastructure/reviewApi';
import {
  claimChallenge,
  createChallenge as createServerChallenge,
  fetchChallengeInvite,
  fetchChallenges,
} from '../../infrastructure/challengeApi';
import {
  buildChallengeUrl,
  resolvePendingChallengeUrl,
  saveChallengeToken,
} from '../../infrastructure/challengeLinks';
import {
  fetchActiveTournament,
  fetchTournamentInvite,
  fetchTournamentRanking,
  joinTournament,
} from '../../infrastructure/tournamentApi';
import {
  clearPendingTournamentToken,
  loadPendingTournamentToken,
  saveActiveTournament,
} from '../../infrastructure/tournamentLinks';
import { TournamentInviteScreen } from './TournamentScreens';
import { getGameLocation, getRandomGameLocation } from '../../data/gameLocations';

const STORAGE_KEY = 'nacanaca-community-v1';
const ZONES = ['Cosmovitral', 'Los Portales', 'Alameda Central', 'Paseo Colón', 'Centro Histórico'];
const loadCommunity = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { plays: [], duels: [] }; } catch { return { plays: [], duels: [] }; } };
const saveCommunity = (community) => localStorage.setItem(STORAGE_KEY, JSON.stringify(community));
export default function ExperienceOverlay({ screen, user, location, gps, stats, mode, duel, onUser, onNavigate, onLocate, onStart, onLogout, onZoneSelect }) {
  const [name, setName] = useState(user || '');
  const [email, setEmail] = useState(() => localStorage.getItem('nacanaca-email') || '');
  const [acceptsEventInfo, setAcceptsEventInfo] = useState(false);
  // email -> solo correo + Validar · register -> se revela el nombre solo si el correo es nuevo
  const [onboardingStep, setOnboardingStep] = useState('email');
  const [authMode, setAuthMode] = useState('activation');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [mailNotice, setMailNotice] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [invite, setInvite] = useState(null);
  const [challengeItems, setChallengeItems] = useState([]);
  const [challengeCursor, setChallengeCursor] = useState(null);
  const [challengeError, setChallengeError] = useState('');
  const [isChallengeLoading, setIsChallengeLoading] = useState(false);
  const [copiedChallengeId, setCopiedChallengeId] = useState(null);
  // Cartel animado post-partida: aparece de inmediato al terminar
  // (victoria o derrota), dura 5s y se desvanece para revelar los puntos.
  const [cartelVisible, setCartelVisible] = useState(true);
  const [cartelFading, setCartelFading] = useState(false);
  // Ranking oficial (servidor, top 100). Un fallo de red nunca muestra datos locales como oficiales.
  const [serverRanking, setServerRanking] = useState({ items: [], season: null, isLoading: false, loaded: false, error: '' });
  // Torneo global: activo visible en el mapa + ranking propio con ganador.
  const [activeTournament, setActiveTournament] = useState({ data: null, lastClosed: null, isLoading: false, loaded: false, error: '' });
  const [tournamentBoard, setTournamentBoard] = useState(null);
  const [tournamentBoardLoading, setTournamentBoardLoading] = useState(false);
  const [tournamentBoardError, setTournamentBoardError] = useState('');
  const [tournamentInvite, setTournamentInvite] = useState(null);
  const [tournamentError, setTournamentError] = useState('');
  const [isTournamentLoading, setIsTournamentLoading] = useState(false);
  const [isTournamentJoining, setIsTournamentJoining] = useState(false);
  const [tournamentAlreadyPlayed, setTournamentAlreadyPlayed] = useState(false);
  // Cierre del festival: puntos congelados y review personal (se abre solo una vez).
  const [festivalReview, setFestivalReview] = useState({
    festival: null, seen: true, review: null, username: '', loaded: false, error: '',
  });
  const mapFrameRef = useRef(null);
  const [inSubmap, setInSubmap] = useState(false);
  const [activeMapZone, setActiveMapZone] = useState(null);
  const backToGeneralMap = () => {
    try {
      mapFrameRef.current?.contentWindow?.postMessage({ type: 'nacanaca-close-detail' }, window.location.origin);
    } catch (error) { /* iframe aún no listo: se oculta igual para no bloquear */ }
    setInSubmap(false);
    setActiveMapZone(null);
  };
  useEffect(() => setName(user || ''), [user]);
  useEffect(() => {
    if (!copiedChallengeId) return undefined;
    const timeoutId = window.setTimeout(() => setCopiedChallengeId(null), 2000);
    return () => window.clearTimeout(timeoutId);
  }, [copiedChallengeId]);
  // Cada resultado nuevo muestra el cartel 5s y luego revela los puntos.
  useEffect(() => {
    if (screen !== 'results') return undefined;
    setCartelVisible(true);
    setCartelFading(false);
    const fadeId = window.setTimeout(() => setCartelFading(true), 4400);
    const hideId = window.setTimeout(() => setCartelVisible(false), 5000);
    return () => { window.clearTimeout(fadeId); window.clearTimeout(hideId); };
  }, [screen, stats?.score, stats?.time, mode]);
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin === window.location.origin && event.data?.type === 'nacanaca-map-zone') {
        setInSubmap(Boolean(event.data.inDetail));
        setActiveMapZone(event.data.inDetail ? (event.data.zone || null) : null);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  useEffect(() => {
    if (screen === 'hub') return;
    setInSubmap(false);
    setActiveMapZone(null);
  }, [screen]);
  // Pin GPS del mapa general: solo la zona detectada dentro de su radio
  // lleva pin. Se reenvía al iframe en cada cambio y al cargar el mapa.
  const gpsZone = gps?.status === 'inside' ? (gps.locationId || null) : null;
  const postGpsZone = () => {
    try {
      mapFrameRef.current?.contentWindow?.postMessage({ type: 'nacanaca-gps-location', zone: gpsZone }, window.location.origin);
    } catch (error) { /* iframe aún no listo */ }
  };
  useEffect(() => {
    if (screen !== 'hub') return;
    postGpsZone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, gpsZone]);
  useEffect(() => {
    if (!['hub', 'results', 'review'].includes(screen)) return undefined;
    let cancelled = false;
    fetchReview()
      .then((result) => { if (!cancelled) setFestivalReview({ ...result, loaded: true, error: '' }); })
      .catch((error) => {
        if (cancelled) return;
        setFestivalReview((current) => ({ ...current, error: error.message || 'No se pudo cargar tu review.' }));
      });
    return () => { cancelled = true; };
  }, [screen]);
  // Primera entrada tras el cierre: el review se abre solo. Al mostrarse queda
  // marcado como visto en servidor y ya no vuelve a abrirse automáticamente.
  useEffect(() => {
    if (!festivalReview.loaded || !festivalReview.festival?.reviewEnabled || festivalReview.seen) return;
    if (screen === 'hub') { onNavigate('review'); return; }
    if (screen !== 'review') return;
    setFestivalReview((current) => ({ ...current, seen: true }));
    markReviewSeen().catch(() => { /* se reintenta en la próxima visita */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, festivalReview.loaded, festivalReview.seen, festivalReview.festival]);
  useEffect(() => {
    if (screen !== 'ranking') return;
    let cancelled = false;
    setServerRanking((current) => ({ ...current, isLoading: true, error: '' }));
    fetchRanking(100)
      .then((result) => {
        if (cancelled) return;
        setServerRanking({ items: result.items || [], season: result.season || null, isLoading: false, loaded: true, error: '' });
      })
      .catch((error) => {
        if (cancelled) return;
        setServerRanking((current) => ({ ...current, isLoading: false, error: error.message || 'No se pudo cargar el ranking.' }));
      });
    return () => { cancelled = true; };
  }, [screen]);
  useEffect(() => {
    if (screen !== 'challenge') return;
    const token = localStorage.getItem('nacanaca-pending-challenge') || '';
    if (!token) { setChallengeError('Esta invitación ya no está disponible.'); return; }
    let cancelled = false;
    setIsChallengeLoading(true);
    fetchChallengeInvite(token)
      .then(({ challenge: item }) => { if (!cancelled) setInvite(item); })
      .catch((error) => { if (!cancelled) setChallengeError(error.message); })
      .finally(() => { if (!cancelled) setIsChallengeLoading(false); });
    return () => { cancelled = true; };
  }, [screen]);
  useEffect(() => {
    if (screen !== 'pending') return;
    let cancelled = false;
    setIsChallengeLoading(true);
    fetchChallenges({ limit: 25 })
      .then((result) => { if (!cancelled) { setChallengeItems(result.items || []); setChallengeCursor(result.nextCursor || null); } })
      .catch((error) => { if (!cancelled) setChallengeError(error.message); })
      .finally(() => { if (!cancelled) setIsChallengeLoading(false); });
    return () => { cancelled = true; };
  }, [screen]);
  // Torneo activo para el mapa, la invitación y la pestaña del ranking.
  useEffect(() => {
    if (!['hub', 'ranking', 'tournament'].includes(screen)) return;
    let cancelled = false;
    setActiveTournament((current) => ({ ...current, isLoading: true, error: '' }));
    fetchActiveTournament()
      .then(({ tournament, lastClosed }) => {
        if (cancelled) return;
        setActiveTournament({ data: tournament || null, lastClosed: lastClosed || null, isLoading: false, loaded: true, error: '' });
      })
      .catch((error) => {
        if (cancelled) return;
        setActiveTournament((current) => ({ ...current, isLoading: false, error: error.message || 'No se pudo cargar el torneo.' }));
      });
    return () => { cancelled = true; };
  }, [screen]);
  // Pestaña del torneo: puntaje del activo o del último cerrado (ganador).
  useEffect(() => {
    if (screen !== 'ranking') return;
    const tournamentId = activeTournament.data?.id || activeTournament.lastClosed?.id;
    if (!tournamentId || !activeTournament.loaded) return;
    let cancelled = false;
    setTournamentBoardLoading(true);
    setTournamentBoardError('');
    fetchTournamentRanking(tournamentId, 100)
      .then((board) => { if (!cancelled) setTournamentBoard(board); })
      .catch((error) => { if (!cancelled) setTournamentBoardError(error.message || 'No se pudo cargar el torneo.'); })
      .finally(() => { if (!cancelled) setTournamentBoardLoading(false); });
    return () => { cancelled = true; };
  }, [screen, activeTournament.loaded, activeTournament.data, activeTournament.lastClosed]);
  // Invitación del torneo: enlace pendiente o torneo visible para todos.
  useEffect(() => {
    if (screen !== 'tournament') return;
    const pending = loadPendingTournamentToken();
    if (pending) {
      let cancelled = false;
      setIsTournamentLoading(true);
      setTournamentError('');
      setTournamentAlreadyPlayed(false);
      setTournamentInvite(null);
      fetchTournamentInvite(pending)
        .then(({ tournament }) => { if (!cancelled) setTournamentInvite(tournament); })
        .catch((error) => { if (!cancelled) setTournamentError(error.message); })
        .finally(() => { if (!cancelled) setIsTournamentLoading(false); });
      return () => { cancelled = true; };
    }
    if (!activeTournament.loaded) return;
    setTournamentInvite(activeTournament.data || activeTournament.lastClosed);
    if (!activeTournament.data && !activeTournament.lastClosed) {
      setTournamentError('Ahora mismo no hay ningún torneo. Vuelve pronto.');
    }
    return undefined;
  }, [screen, activeTournament.loaded, activeTournament.data, activeTournament.lastClosed]);
  const validEmail = (value) => /^\S+@\S+\.\S+$/.test(String(value || '').trim());
  const validIdentifier = (value) => {
    const trimmed = String(value || '').trim();
    return validEmail(trimmed) || (trimmed.length >= 2 && trimmed.length <= 18);
  };

  // Opt-in de eventos: si marcó la casilla, guarda ese correo en
  // `event_subscribers` sin bloquear el flujo de entrada.
  const subscribeOptinIfAccepted = (emailAddress) => {
    if (!acceptsEventInfo) return;
    const candidate = String(emailAddress || '').trim().toLowerCase();
    if (!validEmail(candidate)) return;
    subscribeEventOptin(candidate, 'inicio-optin').catch(() => {
      // El login sigue aunque el opt-in falle; se reintenta en el siguiente paso.
    });
  };

  const enterDirectly = (result) => {
    // Pase directo: sin código y sin instrucciones, al mapa.
    savePlayerSession(result);
    setName(result.player.username);
    localStorage.setItem('nacanaca-email', result.player.email);
    onUser(result.player.username);
    onNavigate('hub');
  };

  const goToVerification = ({ mode, mailSent, emailAddress }) => {
    setAuthMode(mode);
    setVerificationCode('');
    setVerificationError('');
    setMailNotice(
      mailSent
        ? mode === 'login'
          ? `Te enviamos un enlace y un código a ${emailAddress}. Abre el enlace o escribe el código.`
          : `Te enviamos un código de 6 dígitos a ${emailAddress} para activar tu cuenta.`
        : `No pudimos enviar el correo a ${emailAddress}. Intenta reenviar o avisa al operador.`,
    );
    setOnboardingStep('verification');
  };

  // Paso 1: correo o nombre de usuario + Validar (Enter o clic).
  // Validados en este navegador entran directo, sin código ni instrucciones.
  const continueWithEmail = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!validIdentifier(trimmed)) {
      setEmailError('Escribe tu correo o tu nombre de usuario.');
      return;
    }
    setEmailError('');
    setFormNotice('');
    setIsCheckingEmail(true);
    try {
      const result = await checkIdentifier(trimmed);
      if (!result.exists) {
        if (!validEmail(trimmed)) {
          setEmailError('No encontramos ese nombre. Entra con tu correo para crear tu cuenta.');
          return;
        }
        localStorage.setItem('nacanaca-email', result.email || trimmed.toLowerCase());
        setEmail((result.email || trimmed).toLowerCase());
        // Correo nuevo: revela el campo de nombre de usuario para crearlo como no validado.
        setNameError('');
        setFormNotice('Ese correo aún no está registrado. Elige tu nombre para crear tu cuenta.');
        setOnboardingStep('register');
        return;
      }
      setEmail(result.email);
      if (result.verified) {
        // Pase directo si este navegador ya verificó esta cuenta alguna vez.
        const stored = loadPlayerSession();
        if (stored.sessionToken) {
          try {
            const restored = await restorePlayerSession(result.email, stored.sessionToken);
            enterDirectly(restored);
            return;
          } catch {
            // Dispositivo no reconocido o acceso muy viejo: sigue el flujo con código.
          }
        }
        // Correo existente: manda URL de acceso (+ código) y pasa a verificación.
        const login = await requestLoginLink(result.email);
        goToVerification({ mode: 'login', mailSent: login.mailSent, emailAddress: result.email });
      } else {
        // Registrado pero sin activar: reenvía el código y verifica.
        setName(result.username || '');
        const resent = await resendVerificationCode(result.email);
        goToVerification({ mode: 'activation', mailSent: resent.mailSent, emailAddress: result.email });
      }
    } catch (error) {
      setEmailError(error.message || 'No pudimos validar tu cuenta. Intenta de nuevo.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Paso 2 (solo correos nuevos): crea el jugador como no validado y manda el código.
  const continueWithRegistration = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim();
    if (!validEmail(trimmedEmail)) {
      setOnboardingStep('email');
      setEmailError('Escribe un correo válido para continuar.');
      return;
    }
    if (!trimmedName) { setNameError('Elige un nombre de usuario.'); return; }
    if (trimmedName.length < 2) { setNameError('Mínimo 2 caracteres.'); return; }
    if (trimmedName.length > 18) { setNameError('Máximo 18 caracteres.'); return; }
    setNameError('');
    setIsRegistering(true);
    try {
      const created = await registerPlayer(trimmedEmail, trimmedName);
      localStorage.setItem('nacanaca-email', created.email);
      setEmail(created.email);
      setName(created.username || trimmedName);
      subscribeOptinIfAccepted(created.email);
      goToVerification({ mode: 'activation', mailSent: created.mailSent, emailAddress: created.email });
    } catch (error) {
      if (error.code === 'USERNAME_TAKEN') {
        setNameError(error.message);
      } else if (error.code === 'EMAIL_TAKEN' || error.code === 'ALREADY_EXISTS' || error.status === 409) {
        // Alguien lo registró mientras tanto: trata como acceso existente.
        try {
          const login = await requestLoginLink(trimmedEmail);
          goToVerification({ mode: 'login', mailSent: login.mailSent, emailAddress: trimmedEmail });
        } catch {
          setNameError(error.message);
        }
      } else {
        setNameError(error.message || 'No pudimos crear tu cuenta. Intenta de nuevo.');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const continueWithVerification = async (event) => {
    event.preventDefault();
    if (verificationCode.length !== 6) {
      setVerificationError('Escribe los 6 dígitos del código.');
      return;
    }
    setVerificationError('');
    setIsVerifying(true);
    try {
      const result = await verifyEmailCode(email.trim(), verificationCode);
      if (authMode === 'login') {
        // Ya era cuenta validada: entra directo, sin repetir instrucciones.
        enterDirectly(result);
      } else {
        savePlayerSession(result);
        setName(result.player.username);
        localStorage.setItem('nacanaca-email', result.player.email);
        setOnboardingStep('instructions');
      }
    } catch (error) {
      setVerificationError(error.message || 'Código inválido. Intenta de nuevo.');
    } finally {
      setIsVerifying(false);
    }
  };

  const resendCode = async () => {
    setVerificationError('');
    setIsResending(true);
    try {
      const result = await resendVerificationCode(email.trim());
      setMailNotice(
        result.mailSent
          ? `Reenviamos el código a ${result.email}. Revisa tu bandeja y el spam.`
          : 'No pudimos enviar el correo. Intenta de nuevo en un minuto.',
      );
    } catch (error) {
      setVerificationError(error.message || 'No pudimos reenviar el código.');
    } finally {
      setIsResending(false);
    }
  };
  const enterFromInstructions = () => { const next = name.trim(); if (!next) return; onUser(next); onNavigate('hub'); };
  const createChallenge = async () => {
    setIsChallengeLoading(true);
    setChallengeError('');
    try {
      const { challenge: created } = await createServerChallenge();
      const token = created.token || created.inviteToken;
      saveChallengeToken(created.id, token);
      setChallenge({ ...created, token, url: buildChallengeUrl(token), shared: false });
    } catch (error) {
      setChallengeError(error.message);
    } finally {
      setIsChallengeLoading(false);
    }
  };
  const loadMoreChallenges = async () => {
    setIsChallengeLoading(true);
    setChallengeError('');
    try {
      const result = await fetchChallenges({ limit: 25, cursor: challengeCursor });
      setChallengeItems((items) => [...items, ...(result.items || [])]);
      setChallengeCursor(result.nextCursor || null);
    } catch (error) {
      setChallengeError(error.message);
    } finally {
      setIsChallengeLoading(false);
    }
  };
  const beginChallenge = () => {
    if (!challenge?.shared) return;
    onStart('vs', { challengeToken: challenge.token, challenge });
  };
  const beginInvite = async () => {
    const token = localStorage.getItem('nacanaca-pending-challenge') || '';
    if (!token) return;
    setIsChallengeLoading(true);
    setChallengeError('');
    try {
      const { challenge: claimed } = await claimChallenge(token);
      localStorage.removeItem('nacanaca-pending-challenge');
      onStart('vs', { challengeToken: token, challenge: claimed });
    } catch (error) {
      setChallengeError(error.message);
      setIsChallengeLoading(false);
    }
  };
  const shareChallenge = async (channel) => {
    const message = `¡@${user} te desafía en La Maldición del Ñaca Ñaca! ${challenge.url}`;
    if (channel === 'copy') {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(challenge.url);
    }
    if (channel === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    if (channel === 'messenger') window.open(`fb-messenger://share/?link=${encodeURIComponent(challenge.url)}`, '_blank', 'noopener,noreferrer');
    setChallenge((current) => ({ ...current, shared: true }));
  };
  const sharePendingChallenge = async (item, channel) => {
    const url = resolvePendingChallengeUrl(item);
    if (!url) return;
    const message = `¡@${item.creator.username} te desafía en La Maldición del Ñaca Ñaca! ${url}`;
    try {
      if (channel === 'copy') {
        if (!navigator.clipboard) return;
        await navigator.clipboard.writeText(url);
        setCopiedChallengeId(item.id);
      }
      if (channel === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
      }
      if (channel === 'messenger') {
        window.open(`fb-messenger://share/?link=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // El portapapeles puede no estar disponible.
    }
  };

  const joinTournamentFlow = async () => {
    const pending = loadPendingTournamentToken();
    const token = pending || tournamentInvite?.inviteToken || '';
    const tournamentId = token ? undefined : tournamentInvite?.id;
    if (!token && !tournamentId) {
      setTournamentError('No hay torneo disponible para unirse.');
      return;
    }
    setIsTournamentJoining(true);
    setTournamentError('');
    try {
      const { tournament } = await joinTournament({ token: token || undefined, tournamentId });
      const entry = { id: tournament.id, token: tournament.inviteToken || token, tournament };
      saveActiveTournament(entry);
      clearPendingTournamentToken();
      onStart('tournament', { tournament: { ...tournament, token: tournament.inviteToken || token } });
    } catch (error) {
      if (error.code === 'TOURNAMENT_ALREADY_PLAYED') {
        setTournamentAlreadyPlayed(true);
        clearPendingTournamentToken();
      } else {
        setTournamentError(error.message);
      }
    } finally {
      setIsTournamentJoining(false);
    }
  };

  if (screen === 'onboarding') {
    return (
      <OnboardingScreen
        onboardingStep={onboardingStep}
        email={email}
        setEmail={setEmail}
        emailError={emailError}
        setEmailError={setEmailError}
        formNotice={formNotice}
        name={name}
        setName={setName}
        nameError={nameError}
        setNameError={setNameError}
        acceptsEventInfo={acceptsEventInfo}
        setAcceptsEventInfo={setAcceptsEventInfo}
        authMode={authMode}
        mailNotice={mailNotice}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        verificationError={verificationError}
        setVerificationError={setVerificationError}
        isCheckingEmail={isCheckingEmail}
        isRegistering={isRegistering}
        isVerifying={isVerifying}
        isResending={isResending}
        continueWithEmail={continueWithEmail}
        continueWithRegistration={continueWithRegistration}
        continueWithVerification={continueWithVerification}
        resendCode={resendCode}
        enterFromInstructions={enterFromInstructions}
        setOnboardingStep={setOnboardingStep}
        setFormNotice={setFormNotice}
      />
    );
  }

  if (screen === 'hub') {
    // El detalle visible del mapa elige la ambientación. En el mapa general
    // se usa la zona GPS; sin una zona válida se elige una al azar.
    const handleSoloPlay = () => {
      const selected = inSubmap && activeMapZone
        ? getGameLocation(activeMapZone)
        : gpsZone
          ? getGameLocation(gpsZone)
          : getRandomGameLocation();
      if (onZoneSelect) onZoneSelect(selected.gameZone, selected.id);
      onStart('solo');
    };
    return (
      <MapHubScreen
        mapFrameRef={mapFrameRef}
        inSubmap={inSubmap}
        backToGeneralMap={backToGeneralMap}
        gpsZone={gpsZone}
        postGpsZone={postGpsZone}
        gps={gps}
        onLocate={onLocate}
        onNavigate={onNavigate}
        onLogout={onLogout}
        handleSoloPlay={handleSoloPlay}
        activeTournament={activeTournament.data}
        reviewEnabled={Boolean(festivalReview.festival?.reviewEnabled)}
      />
    );
  }

  if (screen === 'locating') {
    return (
      <LocationScreen
        location={location}
        gps={gps}
        onStart={onStart}
        onLocate={onLocate}
        onBack={() => onNavigate('hub')}
      />
    );
  }
  if (screen === 'vs-setup') {
    return (
      <ChallengeSetupScreen
        challenge={challenge}
        error={challengeError}
        loading={isChallengeLoading}
        onCreate={createChallenge}
        onShare={shareChallenge}
        onBegin={beginChallenge}
        onBack={() => onNavigate('hub')}
      />
    );
  }
  if (screen === 'challenge') {
    return (
      <ChallengeInviteScreen
        invite={invite}
        gps={gps}
        error={challengeError}
        loading={isChallengeLoading}
        onLocate={onLocate}
        onBegin={beginInvite}
        onBack={() => onNavigate('hub')}
      />
    );
  }
  if (screen === 'pending') {
    return (
      <PendingChallengesScreen
        items={challengeItems}
        cursor={challengeCursor}
        error={challengeError}
        loading={isChallengeLoading}
        onPlay={(item) => onStart('vs', { challengeId: item.id, challenge: item })}
        onShare={sharePendingChallenge}
        copiedChallengeId={copiedChallengeId}
        onLoadMore={loadMoreChallenges}
        onBack={() => onNavigate('hub')}
      />
    );
  }
  if (screen === 'results' && stats) {
    return (
      <ResultScreen
        stats={stats}
        mode={mode}
        location={location}
        scoringFrozen={Boolean(festivalReview.festival?.frozen)}
        cartelVisible={cartelVisible}
        cartelFading={cartelFading}
        onHideCartel={() => setCartelVisible(false)}
        onNavigate={onNavigate}
        onStart={onStart}
      />
    );
  }
  if (screen === 'credits') return <CreditsScreen onBack={() => onNavigate('hub')} />;
  if (screen === 'review') {
    return (
      <ReviewScreen
        review={festivalReview.review}
        username={festivalReview.username || user}
        loading={!festivalReview.loaded && !festivalReview.error}
        error={festivalReview.loaded ? '' : festivalReview.error}
        onClose={() => onNavigate('hub')}
      />
    );
  }
  if (screen === 'tournament') {
    return (
      <TournamentInviteScreen
        tournament={tournamentInvite}
        error={tournamentError}
        loading={isTournamentLoading}
        joining={isTournamentJoining}
        alreadyPlayed={tournamentAlreadyPlayed}
        onJoin={joinTournamentFlow}
        onBack={() => onNavigate('hub')}
        onRanking={() => onNavigate('ranking')}
      />
    );
  }
  if (screen === 'ranking') {
    return (
      <RankingScreen
        ranking={serverRanking}
        tournamentBoard={tournamentBoard}
        tournamentLoading={tournamentBoardLoading || !activeTournament.loaded}
        tournamentError={tournamentBoardError}
        onBack={() => onNavigate('hub')}
      />
    );
  }
  return null;
}
export { loadCommunity, saveCommunity, ZONES };
